import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../utils/localizedPath.js';
import EventDetailsForm from '../components/EventDetailsForm.js';
import AddParticipantsForm from '../components/AddParticipantsForm.js';
import AddRulesForm from '../components/AddRulesForm.js';
import GenerateAssignments from '../components/GenerateAssignments.js';
import AssignmentPreview from '../components/AssignmentPreview.js';
import EmailInvitations from '../components/EmailInvitations.js';
import LoadingSpinner from '../components/LoadingSpinner.js';
import { randomizeParticipants } from '../utils/multiSolutionAlgorithm.js';
import { groupViolations } from '../utils/violationMapper.js';
import { API_CONFIG } from '../config/api.js';

const ALL_PROHIBITED_CATEGORIES = [
  'Adult content',
  'Alcohol',
  'Weapons',
  'Tobacco',
  'Perishable food',
  'Expensive luxury items',
  'Gag gifts',
  'Political/controversial items',
];

function EventSetupWizard() {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useLocalizedNavigate();
  const [step, setStep] = useState(2);  // Start at step 2 (Event Details) instead of step 1 (welcome page)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [groupId, setGroupId] = useState(eventId || null);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(!!eventId);
  const [isDirty, setIsDirty] = useState(false);  // Track if data has changed since last save
  const [isGenerating, setIsGenerating] = useState(false);  // Loading state for assignment generation
  const [solutionCache, setSolutionCache] = useState({
    solutions: [],        // Array with single solution (regenerate creates fresh solution)
    currentIndex: 0,      // Always 0 (for compatibility)
    generatedAt: null     // Timestamp
  });
  const [formData, setFormData] = useState({
    eventName: '',
    eventDate: '',
    budget: 100,
    message: '',
    eventType: 'kris-kindle',
    country: 'US',
    participants: [],
    csvData: '',
    rules: {},
    inclusions: {},
    prohibitedCategories: [],
    sameBudgetForAll: true,
    budgetsByType: {
      '0-3': 100,
      '4-7': 100,
      '8-12': 100,
      '13-17': 100,
      'Adult': 100,
    },
    sameGiftNumberForAll: true,
    giftsByType: {
      '0-3': 1,
      '4-7': 1,
      '8-12': 1,
      '13-17': 1,
      'Adult': 1,
    },
    numberOfGifts: 1,
    familyUnits: {},
    assignments: {},
    unaccountedPresents: 0,
    showOnlyGivers: true,
    adultsGiveToAdults: true,
    preventReciprocal: true,
    preventMultiplePerFamily: true,
    generatedAssignments: null,
    assignmentError: null,
    validationErrors: null,
    violationsByType: null,          // Grouped violations: { family: [], reciprocal: [], adult: [] }
    hasViolations: false,             // Whether current solution has any violations
    currentSolutionNumber: null,     // Current solution number (1-10)
    totalSolutions: null,            // Total solutions available (usually 10)
    settingsSnapshot: null,  // Snapshot of settings when assignments were generated
  });

  const updateFormData = useCallback((newData) => {
    setFormData(prevFormData => {
      let updatedData = { ...prevFormData, ...newData };

      if (newData.eventType === 'secret-santa') {
        updatedData.prohibitedCategories = ALL_PROHIBITED_CATEGORIES;
      } else if (newData.eventType === 'kris-kindle') {
        updatedData.prohibitedCategories = [];
      }

      // Ensure rules are merged correctly if they are part of newData
      if (newData.rules !== undefined) {
        updatedData.rules = newData.rules;
      }

      return updatedData;
    });
    setIsDirty(true);  // Mark as dirty whenever data changes
  }, []);

  // Load existing draft on mount
  useEffect(() => {
    if (eventId) {
      loadDraft(eventId);
    }
  }, [eventId]);

  const loadDraft = async (id) => {
    try {
      setLoadingDraft(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(API_CONFIG.getUrl(`api/events/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load draft');
      }

      const data = await response.json();

      // Map backend data to formData structure
      setFormData({
        ...formData,
        eventName: data.name || '',
        eventDate: data.eventDate || '',
        eventType: data.eventType || 'kris-kindle',
        budget: data.budget ? parseFloat(data.budget) : 100,
        message: data.message || '',
        country: data.groupCountry || 'US',
        prohibitedCategories: data.prohibitedCategories || [],
        participants: data.participants || [],
        familyUnits: data.familyUnits || {},
        rules: data.rules || {},
        inclusions: data.inclusions || {},
        showOnlyGivers: data.showOnlyGivers !== undefined ? data.showOnlyGivers : true,
        adultsGiveToAdults: data.adultsGiveToAdults !== undefined ? data.adultsGiveToAdults : true,
        preventReciprocal: data.preventReciprocal !== undefined ? data.preventReciprocal : true,
        preventMultiplePerFamily: data.preventMultiplePerFamily !== undefined ? data.preventMultiplePerFamily : true,
        sameBudgetForAll: data.sameBudgetForAll !== undefined ? data.sameBudgetForAll : true,
        budgetsByType: data.budgetsByType || formData.budgetsByType,
        sameGiftNumberForAll: data.sameGiftNumberForAll !== undefined ? data.sameGiftNumberForAll : true,
        giftsByType: data.giftsByType || formData.giftsByType,
        numberOfGifts: data.numberOfGifts !== undefined ? data.numberOfGifts : 1,
        assignments: data.giftCountsPerGiver || {},
        unaccountedPresents: data.unaccountedPresents || 0,
        totalPresents: data.totalPresents || 0,
        generatedAssignments: data.generatedAssignments || null,
        settingsSnapshot: data.settingsSnapshot || null,
      });

      setGroupId(id);
      setIsDirty(false);  // Mark as not dirty after loading
    } catch (error) {
      console.error('Error loading draft:', error);
      // Continue with empty form - user can create a new event
    } finally {
      setLoadingDraft(false);
    }
  };

  const autosaveDraft = async () => {
    try {
      setIsSaving(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const payload = {
        eventName: formData.eventName,
        eventDate: formData.eventDate,
        eventType: formData.eventType,
        budget: formData.budget?.toString(),
        message: formData.message,
        country: formData.country,
        prohibitedCategories: formData.prohibitedCategories,
        participants: formData.participants,
        rules: formData.rules,
        inclusions: formData.inclusions,
        familyUnits: formData.familyUnits,
        showOnlyGivers: formData.showOnlyGivers,
        adultsGiveToAdults: formData.adultsGiveToAdults,
        preventReciprocal: formData.preventReciprocal,
        preventMultiplePerFamily: formData.preventMultiplePerFamily,
        sameBudgetForAll: formData.sameBudgetForAll,
        budgetsByType: formData.budgetsByType,
        sameGiftNumberForAll: formData.sameGiftNumberForAll,
        giftsByType: formData.giftsByType,
        numberOfGifts: formData.numberOfGifts,
        giftCountsPerGiver: formData.assignments,  // Number of gifts each person buys
        unaccountedPresents: formData.unaccountedPresents,
        totalPresents: formData.totalPresents,
        generatedAssignments: formData.generatedAssignments,  // The actual matching
        settingsSnapshot: formData.settingsSnapshot,  // Snapshot when assignments were generated
        isPublished: false,
      };

      let response;
      if (groupId) {
        // Update existing draft
        response = await fetch(API_CONFIG.getUrl(`api/events/${groupId}`), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new draft
        response = await fetch(API_CONFIG.getUrl('api/create-group'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        throw new Error('Failed to save draft');
      }

      const data = await response.json();

      if (!groupId && data.groupId) {
        setGroupId(data.groupId);
        // Update URL without reload
        window.history.replaceState(null, '', `/event/${data.groupId}/edit`);
      }

      console.log('✅ Draft saved');
      setIsDirty(false);  // Mark as not dirty after successful save
    } catch (error) {
      console.error('Error saving draft:', error);
      // Don't block user flow on save error
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }, []);

  const captureSettingsSnapshot = () => {
    // Capture all settings that affect assignments
    return JSON.stringify({
      participants: formData.participants.map(p => ({ name: p.name, age: p.age, isGiver: p.isGiver })),
      rules: formData.rules,
      inclusions: formData.inclusions,
      familyUnits: formData.familyUnits,
      showOnlyGivers: formData.showOnlyGivers,
      adultsGiveToAdults: formData.adultsGiveToAdults,
      preventReciprocal: formData.preventReciprocal,
      preventMultiplePerFamily: formData.preventMultiplePerFamily,
      sameGiftNumberForAll: formData.sameGiftNumberForAll,
      giftsByType: formData.giftsByType,
      numberOfGifts: formData.numberOfGifts,
      assignments: formData.assignments,
    });
  };

  const hasSettingsChanged = () => {
    if (!formData.settingsSnapshot) return false;
    const currentSnapshot = captureSettingsSnapshot();
    return currentSnapshot !== formData.settingsSnapshot;
  };

  /**
   * Display a solution (hide cost, show violations clearly)
   */
  const displaySolution = (solution, solutionNumber, totalSolutions) => {
    // Group violations by type
    const groupedViolations = groupViolations(solution.violations);

    // Determine if there are any violations
    const hasViolations = solution.violations.length > 0;

    updateFormData({
      generatedAssignments: solution.assignments,
      currentSolutionNumber: solutionNumber,
      totalSolutions: totalSolutions,
      violationsByType: groupedViolations,
      hasViolations: hasViolations,
      assignmentError: null,
      validationErrors: null,
      settingsSnapshot: captureSettingsSnapshot()
    });
  };

  /**
   * Prepare gift counts per giver
   */
  const prepareGiftCounts = (participants) => {
    const giftCounts = {};
    participants.forEach(p => {
      if (p.isGiver) {
        // Read from formData.assignments which stores the actual gift count per person
        giftCounts[p.name] = formData.assignments[p.name] || 0;
      }
    });

    return giftCounts;
  };

  /**
   * Calls the server-side assignment solver. The algorithm itself only runs in the
   * generateAssignments Lambda now - this just sends the same eventData shape the
   * client used to compute locally and returns the same {success, assignments, cost,
   * violations, error} contract.
   */
  const callGenerateAssignmentsApi = async (eventData) => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(API_CONFIG.getUrl('api/generate-assignments'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    });

    return response.json();
  };

  /**
   * Generate fresh solution with new optimized algorithm (includes adult chain)
   */
  const handleGenerateAssignments = async () => {
    // Show loading animation
    setIsGenerating(true);

    try {
      // Small delay to ensure loading animation renders
      await new Promise(resolve => setTimeout(resolve, 100));

      // RANDOMIZE participants before generation for variety
      const randomizedParticipants = randomizeParticipants(formData.participants);

      // Validate we have enough data
      const givers = randomizedParticipants.filter(p => p.isGiver);
      if (givers.length === 0) {
        setIsGenerating(false);
        updateFormData({
          assignmentError: 'No givers configured. Please mark at least one participant as a giver.',
          generatedAssignments: null
        });
        return false;
      }

      // Prepare data for new optimized algorithm
      const eventData = {
        participants: randomizedParticipants,
        familyUnits: formData.familyUnits,
        rules: {
          'two-way': formData.rules || {},
          'one-way': {},
          'inclusions': formData.inclusions || {}
        },
        settings: {
          adultsGiveToAdults: formData.adultsGiveToAdults,
          preventReciprocal: formData.preventReciprocal,
          preventMultiplePerFamily: formData.preventMultiplePerFamily
        },
        giftCountsPerGiver: prepareGiftCounts(randomizedParticipants)
      };

      // Generate assignment server-side (includes adult chain)
      const result = await callGenerateAssignmentsApi(eventData);

      if (!result.success) {
        setIsGenerating(false);
        updateFormData({
          assignmentError: result.error || 'Unable to generate valid assignments. Please review your constraints and try again.',
          generatedAssignments: null
        });
        return false;
      }

      // Convert violations to expected format
      const violationsList = [];
      if (result.violations && result.violations.totalCost > 0) {
        Object.values(result.violations.violationsByRule).forEach(ruleViolations => {
          ruleViolations.forEach(v => {
            violationsList.push({
              gifter: v.giver,
              receiver: v.receiver,
              rule: v.type
            });
          });
        });
      }

      // Create solution object compatible with existing display logic
      const solution = {
        assignments: result.assignments,
        cost: result.cost,
        violations: violationsList,
        attempts: 0,
        timedOut: false,
        elapsedTime: 0
      };

      // Cache the solution
      setSolutionCache({
        solutions: [solution],
        currentIndex: 0,
        generatedAt: Date.now()
      });

      // Show the solution
      displaySolution(solution, 1, 1);

      setIsGenerating(false);
      return true;

    } catch (error) {
      console.error('Error generating assignments:', error);
      setIsGenerating(false);
      updateFormData({
        assignmentError: `Error generating assignments: ${error.message}`,
        generatedAssignments: null
      });
      return false;
    }
  };

  /**
   * Generate fresh solution with new random order (using optimized algorithm with adult chain)
   */
  const handleRegenerateAssignments = async () => {
    // Clear cache and generate fresh solution with new random order
    setIsGenerating(true);

    try {
      // Small delay to ensure loading animation renders
      await new Promise(resolve => setTimeout(resolve, 100));

      // RANDOMIZE participants before generation for variety
      const randomizedParticipants = randomizeParticipants(formData.participants);

      // Validate we have enough data
      const givers = randomizedParticipants.filter(p => p.isGiver);
      if (givers.length === 0) {
        setIsGenerating(false);
        updateFormData({
          assignmentError: 'No givers configured. Please mark at least one participant as a giver.',
          generatedAssignments: null
        });
        return;
      }

      // Prepare data for new optimized algorithm
      const eventData = {
        participants: randomizedParticipants,
        familyUnits: formData.familyUnits,
        rules: {
          'two-way': formData.rules || {},
          'one-way': {},
          'inclusions': formData.inclusions || {}
        },
        settings: {
          adultsGiveToAdults: formData.adultsGiveToAdults,
          preventReciprocal: formData.preventReciprocal,
          preventMultiplePerFamily: formData.preventMultiplePerFamily
        },
        giftCountsPerGiver: prepareGiftCounts(randomizedParticipants)
      };

      // Generate assignment server-side (includes adult chain)
      const result = await callGenerateAssignmentsApi(eventData);

      if (!result.success) {
        setIsGenerating(false);
        updateFormData({
          assignmentError: result.error || 'Unable to generate valid assignments. Please review your constraints and try again.',
          generatedAssignments: null
        });
        return;
      }

      // Convert violations to expected format
      const violationsList = [];
      if (result.violations && result.violations.totalCost > 0) {
        Object.values(result.violations.violationsByRule).forEach(ruleViolations => {
          ruleViolations.forEach(v => {
            violationsList.push({
              gifter: v.giver,
              receiver: v.receiver,
              rule: v.type
            });
          });
        });
      }

      // Create solution object compatible with existing display logic
      const solution = {
        assignments: result.assignments,
        cost: result.cost,
        violations: violationsList,
        attempts: 0,
        timedOut: false,
        elapsedTime: 0
      };

      // Cache the new solution
      setSolutionCache({
        solutions: [solution],
        currentIndex: 0,
        generatedAt: Date.now()
      });

      // Show the fresh solution
      displaySolution(solution, 1, 1);

      setIsGenerating(false);

    } catch (error) {
      console.error('Error regenerating assignments:', error);
      setIsGenerating(false);
      updateFormData({
        assignmentError: `Error regenerating assignments: ${error.message}`,
        generatedAssignments: null
      });
    }
  };

  const isStep2Valid = () => {
    if (!formData.eventName || !formData.eventDate || !formData.eventType || !formData.country) {
      return false;
    }

    if (formData.eventType === 'kris-kindle') {
      if (formData.sameBudgetForAll) {
        if (formData.budget === '' || parseFloat(formData.budget) < 0) {
          return false;
        }
      } else {
        for (const type of ['0-3', '4-7', '8-12', '13-17', 'Adult']) {
          if (formData.budgetsByType[type] === '' || parseFloat(formData.budgetsByType[type]) < 0) {
            return false;
          }
        }
      }

      if (formData.sameGiftNumberForAll) {
        if (formData.numberOfGifts === '' || parseInt(formData.numberOfGifts, 10) < 0) {
          return false;
        }
      } else {
        for (const type of ['0-3', '4-7', '8-12', '13-17', 'Adult']) {
          if (formData.giftsByType[type] === '' || parseInt(formData.giftsByType[type], 10) < 0) {
            return false;
          }
        }
      }
    } else { // secret-santa
      if (formData.budget === '' || parseFloat(formData.budget) < 0) {
        return false;
      }
    }

    return true;
  };

  const nextStep = async () => {
    // Only save if data has changed and not already saving
    if (isDirty && !isSaving) {
      await autosaveDraft();
    }
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  // Scroll to top of page whenever the wizard step changes, after the new
  // step's content has rendered (avoids landing mid-page on taller/shorter steps).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [step]);

  const handlePublishClick = () => {
    setShowPublishConfirm(true);
  };

  const handlePublishConfirm = async () => {
    setShowPublishConfirm(false);

    try {
      setIsSaving(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // Prepare publish payload with isPublished = true
      const payload = {
        eventName: formData.eventName,
        eventDate: formData.eventDate,
        eventType: formData.eventType,
        budget: formData.budget?.toString(),
        message: formData.message,
        country: formData.country,
        prohibitedCategories: formData.prohibitedCategories,
        participants: formData.participants,
        rules: formData.rules,
        inclusions: formData.inclusions,
        familyUnits: formData.familyUnits,
        showOnlyGivers: formData.showOnlyGivers,
        adultsGiveToAdults: formData.adultsGiveToAdults,
        preventReciprocal: formData.preventReciprocal,
        preventMultiplePerFamily: formData.preventMultiplePerFamily,
        sameBudgetForAll: formData.sameBudgetForAll,
        budgetsByType: formData.budgetsByType,
        sameGiftNumberForAll: formData.sameGiftNumberForAll,
        giftsByType: formData.giftsByType,
        numberOfGifts: formData.numberOfGifts,
        giftCountsPerGiver: formData.assignments,
        unaccountedPresents: formData.unaccountedPresents,
        totalPresents: formData.totalPresents,
        generatedAssignments: formData.generatedAssignments,
        settingsSnapshot: formData.settingsSnapshot,
        isPublished: true,  // Mark as published
      };

      const response = await fetch(API_CONFIG.getUrl(`api/events/${groupId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to publish event');
      }

      // Navigate to dashboard - event will show as "Published"
      navigate('/dashboard');
    } catch (error) {
      console.error('Error publishing event:', error);
      alert(t('eventSetup.errors.failedToPublish'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublishCancel = () => {
    setShowPublishConfirm(false);
  };

  // Show loading while fetching draft
  if (loadingDraft) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-3xl font-bold mb-4">{t('eventSetup.loading.title')}</h1>
        <p>{t('eventSetup.loading.message')}</p>
      </div>
    );
  }

  // Saving indicator component
  const SavingIndicator = () => {
    if (!isSaving) return null;
    return (
      <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {t('eventSetup.buttons.savingDraft')}
      </div>
    );
  };

  switch (step) {
    case 1:
      return (
        <>
          <SavingIndicator />
          <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-4">{t('eventSetup.welcome.title')}</h1>
            <p className="mb-4">{t('eventSetup.welcome.intro')}</p>
            <p className="mb-4">{t('eventSetup.welcome.description')}</p>
            <ul className="list-disc list-inside mb-4">
              <li>{t('eventSetup.welcome.features.details')}</li>
              <li>{t('eventSetup.welcome.features.participants')}</li>
              <li>{t('eventSetup.welcome.features.rules')}</li>
            </ul>
            <button onClick={nextStep} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
              {t('eventSetup.welcome.start')}
            </button>
          </div>
        </>
      );
    case 2:
      return (
        <>
          <SavingIndicator />
          <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-4">{t('eventSetup.details.title')}</h1>
            <EventDetailsForm formData={formData} updateFormData={updateFormData} />
            <button onClick={() => navigate('/dashboard')} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2">
              {t('eventSetup.buttons.cancel')}
            </button>
            <button onClick={nextStep} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" disabled={!isStep2Valid() || isSaving}>
              {isSaving ? t('eventSetup.buttons.saving') : t('eventSetup.buttons.next')}
            </button>
          </div>
        </>
      );
    case 3:
      return (
        <>
          <SavingIndicator />
          <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-4">{t('eventSetup.participants.title')}</h1>
            <AddParticipantsForm formData={formData} updateFormData={updateFormData} />
            <button onClick={prevStep} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2">
              {t('eventSetup.buttons.back')}
            </button>
            <button onClick={nextStep} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" disabled={isSaving}>
              {isSaving ? t('eventSetup.buttons.saving') : t('eventSetup.buttons.next')}
            </button>
          </div>
        </>
      );
    case 4:
      return (
        <>
          <SavingIndicator />
          <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-4">{t('eventSetup.rules.title')}</h1>
            <AddRulesForm formData={formData} updateFormData={updateFormData} />
            <button onClick={prevStep} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2">
              {t('eventSetup.buttons.back')}
            </button>
            <button onClick={nextStep} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" disabled={isSaving}>
              {isSaving ? t('eventSetup.buttons.saving') : t('eventSetup.buttons.next')}
            </button>
          </div>
        </>
      );
    case 5:
      return (
        <div className="container mx-auto px-4 py-8">
          {/* Loading Spinner */}
          {isGenerating && (
            <LoadingSpinner
              message={`Finding optimal combinations for ${formData.participants.length} participants`}
            />
          )}

          <h1 className="text-3xl font-bold mb-4">{t('eventSetup.generate.title')}</h1>
          <GenerateAssignments formData={formData} updateFormData={updateFormData} />

          {formData.assignmentError && (
            <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              <h3 className="font-bold mb-2">{t('eventSetup.generate.error.title')}</h3>
              <p className="mb-3">{formData.assignmentError}</p>

              {formData.validationErrors && formData.validationErrors.length > 0 && (
                <div className="mt-4 space-y-3">
                  <h4 className="font-semibold text-sm">Problems Found:</h4>
                  {formData.validationErrors.map((err, idx) => (
                    <div key={idx} className="bg-red-50 border border-red-300 rounded p-3">
                      <p className="font-medium mb-1">{err.message}</p>

                      {err.details && (
                        <div className="text-xs mt-2 bg-white bg-opacity-50 p-2 rounded">
                          {err.details.blockedReceivers && err.details.blockedReceivers.length > 0 && (
                            <div className="mb-2">
                              <strong>Blocked receivers:</strong>
                              <ul className="ml-4 mt-1">
                                {err.details.blockedReceivers.map((br, i) => (
                                  <li key={i}>• {br.name}: {br.reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {err.details.blockedGivers && err.details.blockedGivers.length > 0 && (
                            <div className="mb-2">
                              <strong>Blocked givers:</strong>
                              <ul className="ml-4 mt-1">
                                {err.details.blockedGivers.map((bg, i) => (
                                  <li key={i}>• {bg.name}: {bg.reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {err.details.blockedAdults && err.details.blockedAdults.length > 0 && (
                            <div className="mb-2">
                              <strong>Blocked adult receivers:</strong>
                              <ul className="ml-4 mt-1">
                                {err.details.blockedAdults.map((ba, i) => (
                                  <li key={i}>• {ba.name}: {ba.reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {err.suggestions && err.suggestions.length > 0 && (
                        <div className="mt-2">
                          <strong className="text-xs">Suggestions:</strong>
                          <ul className="ml-4 mt-1 text-xs">
                            {err.suggestions.map((suggestion, i) => (
                              <li key={i}>• {suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Perfect Assignment Banner */}
          {!formData.hasViolations && formData.generatedAssignments && (
            <div className="mt-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
              <h3 className="font-bold text-green-900 mb-1 flex items-center">
                <span className="text-xl mr-2">✅</span>
                Perfect Assignment!
              </h3>
              <p className="text-sm text-green-800">
                All constraints satisfied - no rule violations.
              </p>
            </div>
          )}

          {/* Violation Summary Banner (NO COST SHOWN) */}
          {formData.hasViolations && formData.violationsByType && (
            <div className="mt-4 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
              <h3 className="font-bold text-amber-900 mb-2 flex items-center">
                <span className="text-xl mr-2">⚠️</span>
                This Assignment Has Rule Violations
              </h3>
              <p className="text-sm text-amber-800 mb-3">
                The following constraints could not be satisfied. Review and decide if these are acceptable:
              </p>

              {/* Family Violations */}
              {formData.violationsByType.family.length > 0 && (
                <div className="mb-3 bg-yellow-100 border border-yellow-300 p-3 rounded">
                  <h4 className="font-semibold text-sm mb-2 text-yellow-900 flex items-center">
                    👨‍👩‍👧‍👦 Multiple Family Members ({formData.violationsByType.family.length})
                  </h4>
                  <p className="text-xs text-yellow-800 mb-2">
                    These givers are buying for more than one person in the same family:
                  </p>
                  <ul className="text-xs space-y-1">
                    {formData.violationsByType.family.map((v, i) => (
                      <li key={i} className="text-yellow-900 bg-yellow-50 p-2 rounded">
                        • <strong>{v.gifter}</strong> → <strong>{v.receiver}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Reciprocal Violations */}
              {formData.violationsByType.reciprocal.length > 0 && (
                <div className="mb-3 bg-orange-100 border border-orange-300 p-3 rounded">
                  <h4 className="font-semibold text-sm mb-2 text-orange-900 flex items-center">
                    🔄 Reciprocal Gifting ({formData.violationsByType.reciprocal.length})
                  </h4>
                  <p className="text-xs text-orange-800 mb-2">
                    These people are buying gifts for each other:
                  </p>
                  <ul className="text-xs space-y-1">
                    {formData.violationsByType.reciprocal.map((v, i) => (
                      <li key={i} className="text-orange-900 bg-orange-50 p-2 rounded">
                        • <strong>{v.gifter}</strong> ↔ <strong>{v.receiver}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Adult Rule Violations */}
              {formData.violationsByType.adult.length > 0 && (
                <div className="bg-red-100 border border-red-300 p-3 rounded">
                  <h4 className="font-semibold text-sm mb-2 text-red-900 flex items-center">
                    🎯 Adult Gift Rule ({formData.violationsByType.adult.length})
                  </h4>
                  <p className="text-xs text-red-800 mb-2">
                    These adults are not buying for at least one other adult:
                  </p>
                  <ul className="text-xs space-y-1">
                    {formData.violationsByType.adult.map((v, i) => (
                      <li key={i} className="text-red-900 bg-red-50 p-2 rounded">
                        • <strong>{v.gifter}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-3 p-3 bg-white rounded border border-amber-200">
                <p className="text-xs text-gray-700">
                  💡 <strong>Tip:</strong> If these violations are not acceptable, click "Regenerate Assignments"
                  to try a different combination, or adjust your rules to allow more flexibility.
                </p>
              </div>
            </div>
          )}

          {/* Regenerate Button */}
          {formData.generatedAssignments && (
            <div className="mt-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div>
                <p className="text-sm text-blue-900 font-semibold">
                  Showing assignment option <strong>{formData.currentSolutionNumber}</strong> of <strong>{formData.totalSolutions}</strong>
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Not satisfied? Try another combination.
                </p>
              </div>

              <button
                onClick={handleRegenerateAssignments}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg flex items-center gap-2 transition-colors"
              >
                <span className="text-xl">🔄</span>
                Regenerate Assignments
              </button>
            </div>
          )}

          {/* Show info if assignments exist and settings changed */}
          {formData.generatedAssignments && hasSettingsChanged() && (
            <div className="mt-4 p-4 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
              <p className="font-semibold mb-2">{t('eventSetup.generate.settingsChanged.title')}</p>
              <p className="text-sm">{t('eventSetup.generate.settingsChanged.description')}</p>
            </div>
          )}

          <div className="mt-4">
            <button onClick={prevStep} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2">
              {t('eventSetup.buttons.back')}
            </button>

            {/* If assignments exist and no changes, allow to proceed without regenerating */}
            {formData.generatedAssignments && !hasSettingsChanged() ? (
              <button
                onClick={nextStep}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                {t('eventSetup.generate.buttons.continueExisting')}
              </button>
            ) : formData.generatedAssignments && hasSettingsChanged() ? (
              <>
                <button
                  onClick={nextStep}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded mr-2"
                >
                  {t('eventSetup.generate.buttons.keepExisting')}
                </button>
                <button
                  onClick={async () => {
                    const success = await handleGenerateAssignments();
                    if (success) {
                      nextStep();
                    }
                  }}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  {t('eventSetup.generate.buttons.regenerate')}
                </button>
              </>
            ) : (
              <button
                onClick={async () => {
                  const success = await handleGenerateAssignments();
                  if (success) {
                    nextStep();
                  }
                }}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                {t('eventSetup.generate.buttons.generate')}
              </button>
            )}
          </div>
        </div>
      );
    case 6:
      return (
        <div className="container mx-auto px-4 py-8">
          {/* Loading Spinner for Regeneration */}
          {isGenerating && (
            <LoadingSpinner
              message={`Regenerating assignments for ${formData.participants.length} participants`}
            />
          )}

          <h1 className="text-3xl font-bold mb-4">{t('eventSetup.preview.title')}</h1>
          <AssignmentPreview
            formData={formData}
            onRegenerate={async () => {
              await handleRegenerateAssignments();
            }}
          />
          <div className="mt-6">
            <button onClick={prevStep} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2">
              {t('eventSetup.buttons.back')}
            </button>
            <button onClick={handlePublishClick} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">
              {t('eventSetup.preview.publish')}
            </button>
          </div>

          {/* Publish Confirmation Modal */}
          {showPublishConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">{t('eventSetup.preview.confirmPublish.title')}</h3>
                <p className="text-gray-700 mb-6">
                  {t('eventSetup.preview.confirmPublish.message')}
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handlePublishCancel}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                  >
                    {t('eventSetup.buttons.cancel')}
                  </button>
                  <button
                    onClick={handlePublishConfirm}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                  >
                    {t('eventSetup.preview.confirmPublish.confirm')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    case 7:
      return (
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-4">{t('eventSetup.email.title')}</h1>
          <EmailInvitations assignments={formData.generatedAssignments} />
          <button onClick={prevStep} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2">
            {t('eventSetup.buttons.back')}
          </button>
          {/* No Next button as this is the last step of the wizard */}
        </div>
      );
    default:
      return (
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-4">{t('eventSetup.welcome.title')}</h1>
          <p>{t('eventSetup.errors.somethingWrong')}</p>
        </div>
      );
  }
}

export default EventSetupWizard;
